import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp, TrendingDown, Wallet, Receipt, Clock, Calculator,
  Banknote, AlertCircle, DollarSign, Activity, BarChart3,
  ArrowUpRight, ArrowDownRight, Gauge, Heart,
} from 'lucide-react';
import WorkshopOpsChartCard from './WorkshopOpsChartCard';
import OnLeaveBanner from '@/components/OnLeaveBanner';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmt = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt0 = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function ExecutiveSummaryTab({ BACKEND_URL }) {
  const today = new Date();
  const [view, setView] = useState('monthly');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState(1);
  const [rangeStart, setRangeStart] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
  const [rangeEnd, setRangeEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [arDashboard, setArDashboard] = useState(null);

  // Accounts receivable summary (Super Admin only — endpoint already gates on role)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/accounts/dashboard`, { credentials: 'include' });
        if (res.ok) setArDashboard(await res.json());
      } catch (_e) { /* noop */ }
    })();
  }, [BACKEND_URL]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range') {
        params.append('start_date', rangeStart);
        params.append('end_date', rangeEnd);
      } else if (view === 'daily') {
        params.set('start_date', rangeStart);
      } else if (view === 'yearly') {
        params.append('year', year);
      } else {
        params.append('year', year);
        params.append('month', month);
        if (view === 'weekly') params.append('week', week);
      }
      const res = await fetch(`${BACKEND_URL}/api/super-admin/financials/executive-summary?${params}`,
        { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL, view, year, month, week, rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <Card className="p-6"><div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
      </div></Card>
    );
  }
  if (!data) return null;

  const { banking, expenses, gst, productivity, tax, pnl, cash_flow_position: cfp, personal, combined } = data;
  const isProfit = pnl.net_profit >= 0;

  return (
    <div className="space-y-6" data-testid="executive-summary-tab">
      {/* Filters */}
      <Card className="p-4 bg-gradient-to-r from-slate-50 to-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-600" />
              Executive Summary
            </h3>
            <p className="text-xs text-slate-500">
              {fmtDate(data.start_date)} → {fmtDate(data.end_date)} · one-glance business health
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[130px]" data-testid="exec-view-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Annually</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>
            {view === 'daily' && (
              <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="exec-daily-date" />
            )}
            {(view === 'weekly' || view === 'monthly' || view === 'yearly') && (
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {(view === 'weekly' || view === 'monthly') && (
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {view === 'weekly' && (
              <Select value={week.toString()} onValueChange={(v) => setWeek(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((w) => <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {view === 'range' && (
              <>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="exec-range-start" />
                <span className="text-slate-400">to</span>
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-[160px]" data-testid="exec-range-end" />
              </>
            )}
          </div>
        </div>
      </Card>

      {/* KPI tiles — moved to top */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
        {arDashboard && (
          <a
            href="/accounts"
            className="block group"
            data-testid="kpi-accounts-receivable"
          >
            <Card className={`p-4 border-l-4 ${arDashboard.overdue_amount > 0 ? 'border-rose-500' : 'border-indigo-500'} h-full hover:shadow-md transition-shadow cursor-pointer`}>
              <div className="flex items-center gap-2 mb-2">
                <Wallet className={`h-5 w-5 ${arDashboard.overdue_amount > 0 ? 'text-rose-600' : 'text-indigo-600'}`} />
                <h4 className={`text-xs uppercase tracking-wider font-semibold ${arDashboard.overdue_amount > 0 ? 'text-rose-700' : 'text-indigo-700'}`}>
                  Accounts Receivable
                </h4>
              </div>
              <p className={`text-2xl font-bold ${arDashboard.overdue_amount > 0 ? 'text-rose-900' : 'text-indigo-900'}`}>
                {fmt0(arDashboard.total_outstanding)}
              </p>
              <div className="text-xs text-slate-600 mt-2 space-y-0.5">
                <p>Overdue: <strong className={arDashboard.overdue_amount > 0 ? 'text-rose-700' : ''}>{fmt(arDashboard.overdue_amount)}</strong></p>
                <p>Active: <strong>{arDashboard.active_accounts}</strong> · Open invoices: <strong>{arDashboard.open_invoices_count}</strong></p>
                <p className="text-indigo-600 group-hover:underline pt-1">View accounts →</p>
              </div>
            </Card>
          </a>
        )}

        <Card className="p-4 border-l-4 border-blue-500" data-testid="kpi-banking">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="h-5 w-5 text-blue-600" />
            <h4 className="text-xs uppercase tracking-wider font-semibold text-blue-700">Total Income</h4>
          </div>
          <p className="text-2xl font-bold text-blue-900">{fmt0(banking.total_income)}</p>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <p>Banking: <strong>{fmt(banking.total_banking)}</strong></p>
            <p>Cash: <strong>{fmt(banking.cash_in)}</strong></p>
            <p>Transfer: <strong>{fmt(banking.transfer)}</strong></p>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-rose-500" data-testid="kpi-expense">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-rose-600" />
            <h4 className="text-xs uppercase tracking-wider font-semibold text-rose-700">Total Expense</h4>
          </div>
          <p className="text-2xl font-bold text-rose-900">{fmt0(expenses.total_expense)}</p>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <p>Parts: <strong>{fmt(expenses.parts)}</strong></p>
            <p>Entertainment: <strong>{fmt(expenses.entertainment)}</strong></p>
            <p>Others: <strong>{fmt(expenses.others)}</strong></p>
            <p>Cash: <strong>{fmt(expenses.cash_expense)}</strong></p>
            <p className="pt-1 border-t">Fixed (prorated): <strong className="text-rose-700">{fmt(expenses.fixed_expense)}</strong></p>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-amber-500" data-testid="kpi-gst">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="h-5 w-5 text-amber-600" />
            <h4 className="text-xs uppercase tracking-wider font-semibold text-amber-700">GST</h4>
          </div>
          <p className={`text-2xl font-bold ${gst.outstanding >= 0 ? 'text-amber-900' : 'text-emerald-900'}`}>
            {gst.outstanding < 0 && '−'}{fmt0(Math.abs(gst.outstanding))}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {gst.outstanding >= 0 ? 'outstanding to ATO' : 'refund due'}
          </p>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <p>On sales: <strong className="text-blue-700">{fmt(gst.on_sales)}</strong></p>
            <p>On purchases: <strong className="text-amber-700">{fmt(gst.on_purchases)}</strong></p>
            <p>Net owed: <strong>{fmt(gst.net_owed)}</strong></p>
            <p className="pt-1 border-t">Paid: <strong className="text-emerald-700">{fmt(gst.paid_in_period)}</strong></p>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-purple-500" data-testid="kpi-productivity">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-purple-600" />
            <h4 className="text-xs uppercase tracking-wider font-semibold text-purple-700">Productivity</h4>
          </div>
          <p className="text-2xl font-bold text-purple-900">{productivity.performance_pct.toFixed(0)}%</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            of weekday target
          </p>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <p>Expected: <strong>{productivity.expected_hours.toFixed(0)}h</strong></p>
            <p>Actual: <strong className="text-emerald-700">{productivity.actual_hours.toFixed(0)}h</strong></p>
            <p>Deficit: <strong className="text-rose-700">{productivity.deficit_hours.toFixed(0)}h</strong></p>
            <p className="pt-1 border-t">Revenue loss: <strong className="text-rose-700">{fmt(productivity.estimated_revenue_loss)}</strong></p>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-slate-600" data-testid="kpi-tax">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-5 w-5 text-slate-700" />
            <h4 className="text-xs uppercase tracking-wider font-semibold text-slate-700">Tax (est.)</h4>
          </div>
          <p className={`text-2xl font-bold ${tax.pre_tax_profit > 0 ? 'text-slate-900' : 'text-slate-500'}`}>
            {fmt0(tax.estimated_tax)}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">@ {tax.tax_rate_pct}% company rate</p>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <p>Pre-tax profit: <strong>{fmt(tax.pre_tax_profit)}</strong></p>
            <p className="pt-1 border-t text-[10px] italic text-slate-500">
              {tax.pre_tax_profit > 0 ? 'Approximate — consult your accountant' : 'No tax (loss)'}
            </p>
          </div>
        </Card>
      </div>

      {/* Hero Net P&L card */}
      <Card className={`p-6 border-2 ${isProfit ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50' : 'border-rose-300 bg-gradient-to-br from-rose-50 to-red-50'}`} data-testid="hero-pnl-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className={`text-xs uppercase tracking-wider font-semibold ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}>
              Net {isProfit ? 'Profit' : 'Loss'} (after est. tax)
            </p>
            <p className={`text-5xl font-black mt-1 ${isProfit ? 'text-emerald-900' : 'text-rose-900'}`}>
              {!isProfit && '−'}{fmt0(Math.abs(pnl.net_profit))}
            </p>
            <p className={`text-sm mt-1 ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}>
              {isProfit ? <TrendingUp className="inline h-4 w-4 mr-1" /> : <TrendingDown className="inline h-4 w-4 mr-1" />}
              {fmt(pnl.total_income)} income − {fmt(pnl.total_expense)} expense − {fmt(gst.net_owed)} GST − {fmt(pnl.estimated_tax)} tax
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <p className="text-xs text-slate-600 uppercase">Pre-tax</p>
              <p className="text-xl font-bold text-slate-900">{fmt0(pnl.pre_tax_profit)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-600 uppercase">Tax @ 25%</p>
              <p className="text-xl font-bold text-slate-900">{fmt0(pnl.estimated_tax)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-600 uppercase">Margin</p>
              <p className="text-xl font-bold text-slate-900">
                {pnl.total_income > 0 ? ((pnl.net_profit / pnl.total_income) * 100).toFixed(1) : '0'}%
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Cash Flow Position card moved to Profit & Loss tab */}

      {/* Two-up row: Profit & Loss Analysis (left) + Workshop Operations View (right) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="pnl-ops-row">
        <Card className="p-6" data-testid="pnl-waterfall">
          <h4 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            Profit &amp; Loss Analysis
          </h4>
          <PnlWaterfall pnl={pnl} expenses={expenses} gst={gst} />
        </Card>

        <div data-testid="workshop-ops-card-wrapper">
          <WorkshopOpsChartCard defaultDays={14} />
        </div>
      </div>

      {/* iter140 — 3rd widget: On Leave / Upcoming approved leaves.
          User intent: "show all leaves approved regardles of condition…
          show in overview tab of super admin as well below profit and loss
          analysis, workshop overview as third widget" */}
      <div data-testid="overview-on-leave-card-wrapper">
        <OnLeaveBanner variant="card" />
      </div>

      {/* Insight banner */}
      <Card className={`p-4 border-l-4 ${isProfit ? 'border-emerald-500 bg-emerald-50/50' : 'border-rose-500 bg-rose-50/50'}`} data-testid="insight-banner">
        <div className="flex items-start gap-3">
          {isProfit ? (
            <TrendingUp className="h-5 w-5 text-emerald-600 mt-0.5" />
          ) : (
            <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5" />
          )}
          <div className="text-sm">
            <p className={`font-semibold ${isProfit ? 'text-emerald-900' : 'text-rose-900'}`}>
              {isProfit
                ? `Workshop is profitable this period — ${fmt0(pnl.net_profit)} net after tax.`
                : `Workshop is running at a loss of ${fmt0(Math.abs(pnl.net_profit))} this period.`}
            </p>
            <p className={`text-xs mt-1 ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}>
              {productivity.deficit_hours > 10 && (
                <>Closing the <strong>{productivity.deficit_hours.toFixed(0)}h</strong> productivity deficit would add <strong>{fmt(productivity.estimated_revenue_loss)}</strong> in potential revenue. </>
              )}
              {gst.outstanding > 0 && (
                <>BAS outstanding: <strong>{fmt(gst.outstanding)}</strong>. </>
              )}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Simple horizontal waterfall — Income → minus expense components → pre-tax → tax → Net
function PnlWaterfall({ pnl, expenses, gst }) {
  const income = Math.max(0, pnl.total_income);
  const steps = [
    { label: 'Total Income', value: income, type: 'total', color: 'bg-blue-500', text: 'text-white' },
    { label: 'Parts', value: -expenses.parts, type: 'expense', color: 'bg-rose-400', text: 'text-white' },
    { label: 'Entertainment', value: -expenses.entertainment, type: 'expense', color: 'bg-rose-400', text: 'text-white' },
    { label: 'Others', value: -expenses.others, type: 'expense', color: 'bg-rose-400', text: 'text-white' },
    { label: 'Cash expense', value: -expenses.cash_expense, type: 'expense', color: 'bg-rose-400', text: 'text-white' },
    { label: 'Fixed (prorated)', value: -expenses.fixed_expense, type: 'expense', color: 'bg-rose-500', text: 'text-white' },
    { label: 'Pre-tax profit', value: pnl.pre_tax_profit, type: 'subtotal', color: pnl.pre_tax_profit >= 0 ? 'bg-amber-500' : 'bg-rose-700', text: 'text-white' },
    { label: 'Est. tax @ 25%', value: -pnl.estimated_tax, type: 'expense', color: 'bg-slate-500', text: 'text-white' },
    { label: 'Net profit', value: pnl.net_profit, type: 'net', color: pnl.net_profit >= 0 ? 'bg-emerald-600' : 'bg-rose-700', text: 'text-white' },
  ].filter(s => s.type === 'total' || s.type === 'subtotal' || s.type === 'net' || Math.abs(s.value) > 0.01);

  const maxAbs = Math.max(income, ...steps.map(s => Math.abs(s.value))) || 1;

  return (
    <div className="space-y-1.5" data-testid="pnl-waterfall-rows">
      {steps.map((s, i) => {
        const pctWidth = (Math.abs(s.value) / maxAbs) * 100;
        const align = s.value < 0 ? 'ml-0' : '';
        return (
          <div key={i} className={`flex items-center gap-3`}>
            <span className={`text-xs w-44 text-right ${s.type === 'subtotal' || s.type === 'net' ? 'font-bold' : 'text-slate-600'}`}>
              {s.label}
            </span>
            <div className="flex-1 h-7 bg-slate-100 rounded overflow-hidden relative">
              <div
                className={`h-full ${s.color} transition-all duration-500 flex items-center justify-end pr-2 ${align}`}
                style={{ width: `${pctWidth}%` }}
              >
                <span className={`text-[11px] font-semibold ${s.text}`}>
                  {s.value < 0 ? '−' : ''}${Math.abs(s.value).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="pt-3 mt-2 border-t text-[11px] text-slate-500 flex items-center justify-between flex-wrap gap-2">
        <span>Net P&L formula: <strong>Income − Expense − GST owed − Tax</strong> (GST: <strong className="text-amber-700">${gst.net_owed.toLocaleString('en-AU')}</strong>)</span>
        <span className="italic">Tax at flat 25% — approximate only</span>
      </div>
    </div>
  );
}

// Cash Flow Position card — runway gauge + flow waterfall + combined view
function CashFlowPositionCard({ cfp, combined, personal }) {
  const healthColor = {
    healthy: { bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-300', text: 'text-emerald-900', sub: 'text-emerald-700', dot: 'bg-emerald-500' },
    tight:   { bg: 'from-amber-50 to-yellow-50',  border: 'border-amber-300',   text: 'text-amber-900',   sub: 'text-amber-700',   dot: 'bg-amber-500' },
    critical:{ bg: 'from-rose-50 to-red-50',      border: 'border-rose-300',    text: 'text-rose-900',    sub: 'text-rose-700',    dot: 'bg-rose-500' },
  }[cfp.health] || { bg: 'from-slate-50 to-slate-100', border: 'border-slate-300', text: 'text-slate-900', sub: 'text-slate-600', dot: 'bg-slate-400' };

  const inflow = Math.max(0, cfp.cash_in);
  const outflow = Math.max(0, cfp.cash_out);
  const totalAbs = Math.max(inflow, outflow) || 1;
  const inPct = (inflow / totalAbs) * 100;
  const outPct = (outflow / totalAbs) * 100;
  const netPct = (Math.abs(cfp.net_cash_flow) / totalAbs) * 100;

  // Runway gauge: clamp 0-180 days for visual; >180 = full circle (healthy buffer)
  const runwayDisplay = cfp.runway_days != null ? cfp.runway_days : null;
  const gaugePct = runwayDisplay == null ? 100 : Math.min(100, (runwayDisplay / 180) * 100);
  const gaugeStroke = cfp.health === 'healthy' ? '#10b981' : cfp.health === 'tight' ? '#f59e0b' : '#ef4444';
  // SVG circle math: radius 50 → circumference = 2πr ≈ 314.16
  const C = 2 * Math.PI * 50;
  const dashOffset = C - (gaugePct / 100) * C;

  return (
    <Card className={`p-6 border-2 ${healthColor.border} bg-gradient-to-br ${healthColor.bg}`} data-testid="cash-flow-position-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h4 className={`text-base font-bold flex items-center gap-2 ${healthColor.text}`}>
            <Heart className="h-5 w-5" />
            Cash Flow Position
          </h4>
          <p className={`text-xs ${healthColor.sub}`}>
            Live solvency snapshot for the selected period
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 border ${healthColor.border}`}>
          <span className={`h-2 w-2 rounded-full ${healthColor.dot} animate-pulse`} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${healthColor.text}`}>
            {cfp.health}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Runway Gauge */}
        <div className="flex flex-col items-center justify-center" data-testid="runway-gauge">
          <svg width="160" height="160" viewBox="0 0 120 120" className="transform -rotate-90">
            <circle cx="60" cy="60" r="50" stroke="#e2e8f0" strokeWidth="10" fill="none" />
            <circle
              cx="60" cy="60" r="50" fill="none"
              stroke={gaugeStroke} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <div className="-mt-[112px] flex flex-col items-center pointer-events-none">
            <Gauge className={`h-5 w-5 ${healthColor.sub} mb-1`} />
            <span className={`text-2xl font-black ${healthColor.text}`}>
              {runwayDisplay == null ? '∞' : `${Math.round(runwayDisplay)}`}
            </span>
            <span className={`text-[10px] uppercase tracking-wider ${healthColor.sub}`}>
              {runwayDisplay == null ? 'days runway' : 'days runway'}
            </span>
          </div>
          <p className={`text-xs mt-3 ${healthColor.sub} text-center max-w-[180px]`}>
            At ${cfp.burn_per_day.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/day burn
          </p>
        </div>

        {/* Flow waterfall */}
        <div className="md:col-span-2 space-y-3" data-testid="cash-flow-waterfall">
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-emerald-700 flex items-center gap-1">
                <ArrowUpRight className="h-3.5 w-3.5" /> Cash In
              </span>
              <span className="font-bold text-emerald-900">{fmt0(inflow)}</span>
            </div>
            <div className="h-6 bg-slate-100 rounded overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700" style={{ width: `${inPct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-rose-700 flex items-center gap-1">
                <ArrowDownRight className="h-3.5 w-3.5" /> Cash Out
              </span>
              <span className="font-bold text-rose-900">{fmt0(outflow)}</span>
            </div>
            <div className="h-6 bg-slate-100 rounded overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-400 to-rose-600 transition-all duration-700" style={{ width: `${outPct}%` }} />
            </div>
          </div>
          <div className="pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={`font-bold ${cfp.net_cash_flow >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                Net Cash Flow
              </span>
              <span className={`text-xl font-black ${cfp.net_cash_flow >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                {cfp.net_cash_flow < 0 && '−'}{fmt0(Math.abs(cfp.net_cash_flow))}
              </span>
            </div>
            <div className="h-3 bg-slate-100 rounded overflow-hidden">
              <div
                className={`h-full ${cfp.net_cash_flow >= 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-rose-500 to-red-600'} transition-all duration-700`}
                style={{ width: `${netPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Combined personal + business position */}
      {combined && (
        <div className="mt-5 pt-5 border-t border-slate-200/70" data-testid="combined-position-block">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-4 w-4 text-indigo-600" />
            <h5 className="text-sm font-semibold text-indigo-900">Combined Position (Business + Personal)</h5>
            <span className="text-[10px] uppercase tracking-wider text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">private</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Workshop net (period)" value={combined.workshop_net_profit} positive={combined.workshop_net_profit >= 0} />
            <Stat label="Personal surplus (period)" value={combined.personal_period_surplus} positive={combined.personal_period_surplus >= 0} />
            <Stat label="Combined position" value={combined.combined_period_position} positive={combined.combined_period_position >= 0} bold />
            <Stat label="Personal liabilities" value={combined.total_personal_liabilities} positive={false} muted />
          </div>
          {personal && personal.has_data && (
            <p className="text-[11px] text-slate-500 mt-2">
              Personal monthly: <strong>{fmt0(personal.monthly_income)}</strong> income · <strong>{fmt0(personal.monthly_emi)}</strong> EMI · <strong>{fmt0(personal.monthly_expense)}</strong> expenses · surplus <strong className={personal.monthly_surplus >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{fmt0(personal.monthly_surplus)}</strong>/mo
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, positive, bold, muted }) {
  return (
    <div className={`p-3 rounded-lg ${muted ? 'bg-slate-50' : positive ? 'bg-emerald-50' : 'bg-rose-50'}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`${bold ? 'text-xl font-black' : 'text-base font-bold'} ${muted ? 'text-slate-700' : positive ? 'text-emerald-900' : 'text-rose-900'}`}>
        {value < 0 && '−'}{fmt0(Math.abs(value))}
      </p>
    </div>
  );
}
